'use client';

import { QueryKeys } from '@/constants/query-keys';
import { ShowStyles } from '@/enums/show-styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

type LayoutContext = {
  hideLayout: boolean;
  showStyles?: ShowStyles;
};

export const useLayoutContext = (): ReturnType<typeof useQuery<LayoutContext>> => {
  return useQuery({
    enabled: false,
    queryFn: (): Promise<LayoutContext> => {
      // this should never actually run since we'll always have cached data
      // but we need a queryFn for TypeScript
      throw new Error('Layout context issue has occurred');
    },
    queryKey: QueryKeys.global.layout,
    retry: false,
    staleTime: Infinity,
  });
};

export const useUpdateLayoutContext = (): ReturnType<typeof useMutation<void, unknown, LayoutContext>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (layoutContext: LayoutContext) => {
      queryClient.setQueryData(QueryKeys.global.layout, layoutContext);

      return Promise.resolve();
    },
  });
};
export const useApplyLayoutContext = (hideLayout: boolean, showStyles?: ShowStyles): void => {
  const { mutate: setLayoutContext } = useUpdateLayoutContext();

  useEffect(() => {
    setLayoutContext({ hideLayout, showStyles });

    return (): void => {
      setLayoutContext({ hideLayout: false, showStyles: ShowStyles.Main });
    };
  }, [hideLayout, showStyles, setLayoutContext]);
};
